import { NavLink } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { paperStyles } from '@festina-life/ui/libs'
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Typography,
  typographyStyles,
} from '@festina-life/ui/structure'
import cx from 'classnames'

import { Attribute } from '../../components/attribute/Attribute'
import { BlockIndicator } from '../../components/blockIndicator/BlockIndicator'
import { ConfigurationItemIcon } from '../../components/configurationItemIcon/ConfigurationItemIcon'
import { useMetaModelContext } from '../../context/metaModelContext'
import type { configurationItem } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useWorkspaceManagerContext } from '../../workspace/workspaceManagerContext'
import { ConfigurationLeafTags, type ConfigurationTreeLeaf } from './types'

type Props = {
  leaf: ConfigurationTreeLeaf
  isCurrentLayerEdited: boolean
  configurationItemPath: typeof configurationItem
  onHoverStart: () => void
  onHoverEnd: () => void
  onNavigateToBlock?: (blockId: string) => void
}

export const ConfigurationLeaf = ({
  leaf,
  isCurrentLayerEdited,
  configurationItemPath,
  onHoverStart,
  onHoverEnd,
  onNavigateToBlock,
}: Props) => {
  const translate = useTranslate()
  const { currentWorkspaceKey } = useWorkspaceManagerContext()
  const { blockTopology } = useMetaModelContext()
  const { server, branch } = useNavigationParams()
  const baseBlock = blockTopology.name
  const isDraft = leaf.tags.includes(ConfigurationLeafTags.draft)

  const leafContent = (
    <div
      className={cx(
        'group grid grid-cols-[16px_1fr] items-center gap-x-2',
        typographyStyles({
          intensity:
            currentWorkspaceKey && !isCurrentLayerEdited
              ? 'tertiary'
              : 'primary',
        }),
      )}
    >
      <ConfigurationItemIcon type={leaf.type} />
      <div className="flex flex-row items-center gap-x-2">
        <Typography
          className={cx({
            'text-draft-600! dark:text-draft-400! font-medium': isDraft,
          })}
        >
          {leaf.id}
        </Typography>
        {leaf.description && (
          <Typography size="sm" intensity="tertiary">
            {leaf.description}
          </Typography>
        )}
        {leaf.tags.includes(ConfigurationLeafTags.extrinsic) && (
          <Attribute className="text-sm">
            {translate('attribute.extrinsic')}
          </Attribute>
        )}
        {!(leaf.blocks.length === 1 && leaf.blocks[0] === baseBlock) &&
          leaf.blocks.map((block) => (
            <Attribute key={block} className="text-sm">
              {block}
            </Attribute>
          ))}
      </div>
    </div>
  )

  if (leaf.blocks.length === 1) {
    return (
      <div
        onMouseEnter={onHoverStart}
        onMouseLeave={onHoverEnd}
        className="w-full"
      >
        <NavLink
          to={configurationItemPath({
            workspaceName: currentWorkspaceKey?.name,
            server,
            itemId: leaf.id,
            layer: leaf.layer,
            branch,
            type: leaf.type,
            block: leaf.blocks[0],
          })}
          key={leaf.id}
          className="flex w-full justify-start"
        >
          {leafContent}
        </NavLink>
      </div>
    )
  }

  return (
    <Popover
      className="relative w-full"
      key={leaf.id}
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
    >
      <PopoverButton className="w-full text-left">{leafContent}</PopoverButton>
      <PopoverPanel
        isShowingBackdrop
        anchor="bottom start"
        className={cx(
          'z-20 flex flex-col rounded-md shadow-lg p-1',
          paperStyles({ isFaded: true }),
        )}
      >
        {({ close }) => (
          <>
            {leaf.blocks.map((block) => (
              <NavLink
                key={block}
                to={configurationItemPath({
                  workspaceName: currentWorkspaceKey?.name,
                  server,
                  itemId: leaf.id,
                  layer: leaf.layer,
                  branch,
                  type: leaf.type,
                  block,
                })}
                className="rounded-md px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-start"
                onClick={() => {
                  close()
                  onNavigateToBlock?.(block)
                }}
              >
                <BlockIndicator
                  block={block}
                  shouldShowIcon
                  isSecondaryBackground
                  className="bg-inherit!"
                />
              </NavLink>
            ))}
          </>
        )}
      </PopoverPanel>
    </Popover>
  )
}
